import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import BackMethods from "../../back_component/BackMethods";
import { FaFileExcel, FaSearch, FaTable, FaChartLine, FaMapMarkerAlt, FaMoneyBillWave, FaArrowRight, FaTimes } from "react-icons/fa";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const buildFiltersFromParams = (params) => ({
    from: params.get("from") || "",
    to: params.get("to") || "",
    productId: params.get("productId") || "",
    governorate: params.get("governorate") || "",
    city: params.get("city") || "",
    district: params.get("district") || "",
    establishmentUid: params.get("establishmentUid") || "",
    currency: params.get("currency") || "SYP",
});

const PriceSurveyReportPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();

    const returnTo = searchParams.get("returnTo");
    const fromMatrixReport = Boolean(returnTo);

    const [reports, setReports] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const currencyOptions = [
        { value: "SYP", label: "ليرة سورية (SYP)" },
        { value: "USD", label: "دولار أمريكي ($)" }
    ];

    const [filters, setFilters] = useState(() => buildFiltersFromParams(searchParams));
    const [photoModal, setPhotoModal] = useState(null);

    const isSearchDisabled = !filters.from || !filters.to;

    const hasLocationFilter = filters.governorate || filters.city || filters.district;

    // حساب إحصائيات المواد (الوسطي)
    const productStats = useMemo(() => {
        if (!reports || reports.length === 0) return [];
        const stats = reports.reduce((acc, item) => {
            if (!acc[item.productName]) {
                acc[item.productName] = { sum: 0, count: 0 };
            }
            acc[item.productName].sum += item.priceValue;
            acc[item.productName].count += 1;
            return acc;
        }, {});

        return Object.keys(stats).map(name => ({
            name,
            average: filters.currency === "USD"
                ? (stats[name].sum / stats[name].count).toFixed(2)
                : Math.round(stats[name].sum / stats[name].count),
            count: stats[name].count
        }));
    }, [reports, filters.currency]);

    // تجهيز بيانات الرسم البياني
    const chartData = useMemo(() => {
        if (!reports || reports.length === 0) return [];
        return [...reports]
            .sort((a, b) => new Date(a.capturedAtUtc) - new Date(b.capturedAtUtc))
            .map(item => ({
                timestamp: new Date(item.capturedAtUtc).getTime(),
                price: item.priceValue,
                product: item.productName,
                fullDate: new Date(item.capturedAtUtc).toLocaleDateString('ar-SY')
            }));
    }, [reports]);

    const loadEstablishments = async (inputValue) => {
        if (!inputValue) return [];
        try {
            const res = await backEnd_request.get(`/Establishments?q=${inputValue}&page=1&size=1000`);
            return res.data.data.map(est => ({
                value: est.fieldUID,
                label: `${est.name} (${est.district || est.city})`
            }));
        } catch (err) { return []; }
    };

    const fetchProducts = useCallback(async () => {
        try {
            const res = await backEnd_request.get("/PriceSurveyProducts?includeInactive=true");
            setProducts(res.data.map(p => ({ value: p.priceSurveyProductId, label: p.nameAr || p.name })));
        } catch (err) { console.error(err); }
    }, [backEnd_request]);

    const fetchReportData = async (overrideFilters = null) => {
        const activeFilters = overrideFilters || filters;
        if (!activeFilters.from || !activeFilters.to) return;
        setLoading(true);
        try {
            const query = Object.fromEntries(
                Object.entries(activeFilters).filter(([_, v]) => v !== "" && v != null)
            );
            const queryParams = new URLSearchParams(query).toString();
            const res = await backEnd_request.get(`/PriceSurveys/report?${queryParams}`);
            setReports(res.data || []);
            if (overrideFilters) setFilters(activeFilters);
        } catch (err) {
            console.error(err);
            setReports([]);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        const nextFilters = buildFiltersFromParams(searchParams);
        setFilters(nextFilters);
        if (nextFilters.from && nextFilters.to) {
            fetchReportData(nextFilters);
        }
    }, [searchParams]);

    const customStyles = {
        control: (base) => ({ ...base, borderRadius: '8px', minHeight: '42px', border: '1px solid #ced4da' })
    };
    const handleExport = async () => {
        try {
            const query = Object.fromEntries(
                Object.entries({ ...filters, exportXlsx: true }).filter(([k, v]) => v !== "" && v != null)
            );
            const queryParams = new URLSearchParams(query).toString();

            const response = await backEnd_request.get(
                `/PriceSurveys/report?${queryParams}`,
                { responseType: "blob" }
            );

            // إنشاء رابط تحميل
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");

            // اسم الملف (اختياري)
            link.href = url;
            link.setAttribute("download", "price_report.xlsx");

            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            console.error("Export error:", error);
        }
    };
    // const groupedReports = useMemo(() => {
    //     if (!reports.length) return [];

    //     const grouped = {};

    //     reports.forEach(item => {
    //         if (!grouped[item.priceSurveyId]) {
    //             grouped[item.priceSurveyId] = {
    //                 id: item.priceSurveyId,
    //                 establishmentName: item.establishmentName,
    //                 city: item.city,
    //                 date: item.capturedAtUtc,
    //                 products: []
    //             };
    //         }

    //         grouped[item.priceSurveyId].products.push(item);
    //     });

    //     return Object.values(grouped);
    // }, [reports]);

    const groupedByProduct = useMemo(() => {
        if (!reports.length) return [];

        const grouped = {};

        reports.forEach(item => {
            if (!grouped[item.productName]) {
                grouped[item.productName] = {
                    name: item.productName,
                    prices: [],
                    records: []
                };
            }

            grouped[item.productName].prices.push(item.priceValue);
            grouped[item.productName].records.push(item);
        });

        return Object.values(grouped).map(product => {
            const sum = product.prices.reduce((a, b) => a + b, 0);
            const avg = sum / product.prices.length;

            return {
                ...product,
                average: avg,
                max: Math.max(...product.prices),
                min: Math.min(...product.prices),
                count: product.prices.length
            };
        });
    }, [reports]);
    return (
        <div className="container-fluid px-4 py-4" dir="rtl" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    {returnTo && (
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center shadow-sm"
                            onClick={() => navigate(returnTo)}
                        >
                            <FaArrowRight className="ms-2" />
                            عودة للتقرير المصفوفة
                        </button>
                    )}
                    <h3 className="fw-bold text-dark m-0">
                        <FaTable className="ms-2 text-primary" /> تقارير سبر الأسعار
                    </h3>
                </div>
                <button
                    className="btn btn-success shadow-sm d-flex align-items-center fw-bold"
                    disabled={reports.length === 0}
                    onClick={handleExport}
                >
                    <FaFileExcel className="ms-2" /> تصدير Excel
                </button>
            </div>

            {hasLocationFilter && (
                <div className="alert alert-info py-2 px-3 mb-3 small d-flex align-items-center flex-wrap gap-2">
                    <FaMapMarkerAlt />
                    <span className="fw-bold">الموقع:</span>
                    {filters.governorate && <span>{filters.governorate}</span>}
                    {filters.city && <span>— {filters.city}</span>}
                    {filters.district && <span>— {filters.district}</span>}
                </div>
            )}

            {/* Filters */}
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4 text-end">
                    <div className="row g-3">
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2 text-danger">من تاريخ *</label>
                            <input type="date" className="form-control" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2 text-danger">إلى تاريخ *</label>
                            <input type="date" className="form-control" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">المحافظة</label>
                            <input type="text" className="form-control" placeholder="دمشق" value={filters.governorate} onChange={(e) => setFilters({ ...filters, governorate: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">المدينة</label>
                            <input type="text" className="form-control" placeholder="دمشق" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2">القطاع</label>
                            <input type="text" className="form-control" placeholder="المزة" value={filters.district} onChange={(e) => setFilters({ ...filters, district: e.target.value })} />
                        </div>
                        <div className="col-md-2">
                            <label className="small fw-bold mb-2"><FaMoneyBillWave className="ms-1 text-success" /> العملة</label>
                            <Select
                                options={currencyOptions}
                                value={currencyOptions.find((o) => o.value === filters.currency) || currencyOptions[0]}
                                styles={customStyles}
                                onChange={(opt) => setFilters({ ...filters, currency: opt.value })}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold mb-2">المنشأة</label>
                            <AsyncSelect cacheOptions loadOptions={loadEstablishments} placeholder="بحث..." styles={customStyles} isClearable onChange={(opt) => setFilters({ ...filters, establishmentUid: opt?.value || "" })} />
                        </div>
                        <div className="col-md-3">
                            <label className="small fw-bold mb-2">المادة</label>
                            <Select
                                options={products}
                                placeholder="اختر مادة..."
                                isClearable
                                styles={customStyles}
                                value={products.find((p) => String(p.value) === String(filters.productId)) || null}
                                onChange={(opt) => setFilters({ ...filters, productId: opt?.value || "" })}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary w-100 fw-bold shadow-sm" style={{ height: '42px' }} onClick={() => fetchReportData()} disabled={isSearchDisabled || loading}>
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><FaSearch className="ms-2" /> بحث</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {reports.length > 0 && (
                <>
                    {/* بطاقات الإحصائيات */}
                    {/* <div className="row mb-4">
                        {productStats.map((stat, idx) => (
                            <div className="col-md-3 mb-3" key={idx}>
                                <div className="card border-0 shadow-sm rounded-3 bg-white border-start border-success border-4 h-100">
                                    <div className="card-body p-3">
                                        <div className="text-muted small fw-bold mb-1">{stat.name}</div>
                                        <div className="d-flex align-items-baseline">
                                            <h4 className="fw-bold text-success mb-0">{Number(stat.average).toLocaleString()}</h4>
                                            <small className="text-muted ms-2 fw-bold">{filters.currency === "SYP" ? "ل.س" : "$"}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div> */}

                    {/* الرسم البياني - يظهر عند اختيار مادة محددة فقط ليكون ذا معنى */}
                    {/* الرسم البياني */}
                    {filters.productId && !fromMatrixReport && (
                        <div className="card border-0 shadow-sm rounded-4 mb-4 p-4">
                            <h5 className="fw-bold mb-4 text-dark d-flex align-items-center">
                                <FaChartLine className="ms-2 text-info" /> مؤشر تغير السعر ({filters.currency === "SYP" ? "ل.س" : "$"})
                            </h5>
                            <div style={{ width: '100%', height: 350 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis
                                            dataKey="timestamp"
                                            type="number"
                                            domain={['dataMin', 'dataMax']}
                                            tickFormatter={(t) => new Date(t).toLocaleDateString('ar-SY', { day: 'numeric', month: 'short' })}
                                            tick={{ fontSize: 12 }}
                                        />
                                        {/* التعديل هنا: يبدأ من 0 وينتهي عند أعلى قيمة تلقائياً */}
                                        <YAxis
                                            tick={{ fontSize: 12 }}
                                            domain={[0, 'auto']}
                                            tickFormatter={(value) => value.toLocaleString()}
                                        />
                                        <Tooltip
                                            labelFormatter={(t) => `التاريخ: ${new Date(t).toLocaleString('ar-SY')}`}
                                            formatter={(value) => [`${value.toLocaleString()} ${filters.currency === "SYP" ? "ل.س" : "$"}`, "السعر"]}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="price"
                                            stroke="#0d6efd"
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: '#0d6efd' }}
                                            activeDot={{ r: 8 }}
                                            animationDuration={1000}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* الجدول */}
                    {/* <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 text-end">
                                <thead className="bg-light text-secondary">
                                    <tr>
                                        <th className="py-3 ps-4">التاريخ والوقت</th>
                                        <th>المنشأة</th>
                                        <th className="text-center">المادة</th>
                                        <th className="text-center">السعر ({filters.currency})</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((item, index) => (
                                        <tr key={index}>
                                            <td className="ps-4">
                                                <div className="fw-bold">{new Date(item.capturedAtUtc).toLocaleDateString('ar-SY')}</div>
                                                <small className="text-muted"><FaClock className="ms-1" /> {new Date(item.capturedAtUtc).toLocaleTimeString('ar-SY', { hour: '2-digit', minute: '2-digit' })}</small>
                                            </td>
                                            <td>
                                                <div className="fw-bold">{item.establishmentName}</div>
                                                <small className="text-muted">{item.governorate} - {item.city}</small>
                                            </td>
                                            <td className="text-center"><span className="badge bg-primary-subtle text-primary px-3">{item.productName}</span></td>
                                            <td className="text-center">
                                                <div className="fw-bold text-success fs-5">
                                                    {item.priceValue.toLocaleString()}
                                                    <small className="fw-normal text-muted ms-1 small">{filters.currency === "SYP" ? "ل.س" : "$"}</small>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div> */}
                </>
            )}
            {groupedByProduct.map((product, index) => (
                <div key={index} className="card mb-3 shadow-sm rounded-4">
                    <div className="card-body">

                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold text-primary m-0">
                                {product.name}
                            </h5>

                            <div className="text-end">
                                <div className="fw-bold text-success">
                                    {Math.round(product.average).toLocaleString()} {filters.currency}
                                </div>
                                <small className="text-muted">وسطي السعر</small>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="row text-center mb-3">
                            <div className="col">
                                <div className="fw-bold text-success">
                                    {product.max.toLocaleString()}
                                </div>
                                <small>أعلى سعر</small>
                            </div>

                            <div className="col">
                                <div className="fw-bold text-danger">
                                    {product.min.toLocaleString()}
                                </div>
                                <small>أدنى سعر</small>
                            </div>

                            <div className="col">
                                <div className="fw-bold">
                                    {product.count}
                                </div>
                                <small>عدد التسجيلات</small>
                            </div>
                        </div>

                        {/* جدول صغير */}
                        <div className="table-responsive">
                            <table className="table table-sm text-end">
                                <thead>
                                    <tr>
                                        <th>المنشأة</th>
                                        <th>السعر</th>
                                        <th>التاريخ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* {product.records.slice(0, 5).map((r, i) => (
                                         */}
                                    {product.records.map((r, i) => (
                                        <tr
                                            key={r.itemUid || r.priceSurveyId || i}
                                            className={r.photoUrl ? "ps-survey-row--has-photo" : ""}
                                            onClick={() => r.photoUrl && setPhotoModal(r)}
                                            title={r.photoUrl ? "اضغط لعرض صورة السبر" : undefined}
                                        >
                                            <td>{r.establishmentName}</td>
                                            <td className="text-success fw-bold">
                                                {r.priceValue.toLocaleString()}
                                            </td>
                                            <td>
                                                {new Date(r.capturedAtUtc).toLocaleDateString("ar-SY")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>
            ))}
            {!loading && reports.length === 0 && (
                <div className="text-center py-5 bg-white rounded-4 shadow-sm border mt-4">
                    <div className="text-muted mb-2 fs-4">لا توجد بيانات متاحة حالياً</div>
                    <div className="small text-muted">يرجى اختيار النطاق الزمني والضغط على بحث</div>
                </div>
            )}

            {photoModal && (
                <div
                    className="ps-photo-modal-overlay"
                    onClick={() => setPhotoModal(null)}
                    role="presentation"
                >
                    <div
                        className="ps-photo-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-photo-modal__header">
                            <div>
                                <h6 className="mb-0 fw-bold">صورة السبر</h6>
                                <small className="text-muted">{photoModal.establishmentName}</small>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setPhotoModal(null)}
                                aria-label="إغلاق"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-photo-modal__body">
                            <img
                                src={photoModal.photoUrl}
                                alt={`صورة سبر ${photoModal.productName}`}
                                className="ps-photo-modal__img"
                            />
                            <div className="ps-photo-modal__meta">
                                <div className="row g-2 small">
                                    <div className="col-6">
                                        <span className="text-muted">السعر:</span>{" "}
                                        <span className="fw-bold text-success">
                                            {Number(photoModal.priceValue).toLocaleString("ar-SY")}{" "}
                                            {photoModal.currency === "USD" ? "$" : "ل.س"}
                                        </span>
                                    </div>
                                    <div className="col-6">
                                        <span className="text-muted">التاريخ:</span>{" "}
                                        <span className="fw-semibold">
                                            {new Date(photoModal.capturedAtUtc).toLocaleString("ar-SY")}
                                        </span>
                                    </div>
                                    <div className="col-12">
                                        <span className="text-muted">الموقع:</span>{" "}
                                        <span className="fw-semibold">
                                            {[photoModal.governorate, photoModal.city, photoModal.district]
                                                .filter(Boolean)
                                                .join(" — ")}
                                        </span>
                                    </div>
                                    {photoModal.productName && (
                                        <div className="col-12">
                                            <span className="text-muted">المادة:</span>{" "}
                                            <span className="fw-semibold">{photoModal.productName}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="ps-photo-modal__footer">
                            <a
                                href={photoModal.photoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm fw-bold"
                            >
                                فتح الصورة في تبويب جديد
                            </a>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold"
                                onClick={() => setPhotoModal(null)}
                            >
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .ps-survey-row--has-photo { cursor: pointer; }
                .ps-survey-row--has-photo:hover { background: #f0f7ff !important; }
                .ps-photo-modal-overlay {
                    position: fixed; inset: 0; z-index: 2000;
                    background: rgba(15, 23, 42, 0.55);
                    display: flex; align-items: center; justify-content: center;
                    padding: 1rem; backdrop-filter: blur(2px);
                }
                .ps-photo-modal {
                    background: #fff; border-radius: 1rem; width: 100%;
                    max-width: 640px; max-height: 92vh; overflow: hidden;
                    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
                }
                .ps-photo-modal__header {
                    display: flex; justify-content: space-between; align-items: flex-start;
                    padding: 1rem 1.15rem; border-bottom: 1px solid #e2e8f0; background: #f8fafc;
                }
                .ps-photo-modal__body { padding: 1rem; overflow-y: auto; }
                .ps-photo-modal__img {
                    width: 100%; max-height: 60vh; object-fit: contain;
                    border-radius: 0.75rem; background: #f1f5f9; border: 1px solid #e2e8f0;
                }
                .ps-photo-modal__meta {
                    margin-top: 1rem; padding: 0.85rem; background: #f8fafc;
                    border-radius: 0.5rem; border: 1px solid #e2e8f0;
                }
                .ps-photo-modal__footer {
                    display: flex; justify-content: flex-end; gap: 0.5rem;
                    padding: 0.85rem 1.15rem; border-top: 1px solid #e2e8f0; background: #fafbfc;
                }
            `}</style>
        </div>
    );
};

export default PriceSurveyReportPage;